// Constants
import AsyncStorage from '@react-native-async-storage/async-storage';
import { toast } from 'react-toastify';
import i18n from '../i18n/i18n';

const appVersion = '1.0.0';
const acceptVersion = '1.0.0';
const version = process.env.REACT_APP_API_VERSION || 'v1/';

// Environment management
const envName = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
const defaultEnvironment = (process.env.REACT_APP_DEFAULT_ENVIRONMENT || 'PRODUCTION').toUpperCase();
const serverEnvironment = (process.env.REACT_APP_SERVER_ENVIRONMENT || 'server').toLowerCase();

// Default base URL
let currentEnvironment = defaultEnvironment === 'SANDBOX' ? 'SANDBOX' : 'PRODUCTION';
const localhostUrls = {
  localhost_prod: process.env.REACT_APP_LOCAL_PROD_URL || "http://localhost:4100/",
  localhost_dev: process.env.REACT_APP_LOCAL_SANDBOX_URL || "http://localhost:4000/",
};
const apiUrls = {
  prod_domain: serverEnvironment === 'localhost'
    ? localhostUrls.localhost_prod
    : (process.env.REACT_APP_PRODUCTION_BASE_URL || "https://eagle.ethion.cloud/"),
  dev_domain: serverEnvironment === 'localhost'
    ? localhostUrls.localhost_dev
    : (process.env.REACT_APP_SANDBOX_BASE_URL || "https://sandbox.blockmap.dev/"),
  upload_server: currentEnvironment === 'PRODUCTION'
    ? process.env.REACT_APP_TROVILO_ROD_URL || "https://api.trovilo.co:3100/v2/"
    : (process.env.REACT_APP_TROVILO_DEV_URL || "https://api.trovilo.co:8100/v2/"),
  documentation: process.env.REACT_APP_DOCUMENTATION_URL,
  openstreetmap: process.env.REACT_APP_OPENSTREETMAP_URL,
};
let baseUrl = currentEnvironment === 'PRODUCTION' ? apiUrls.prod_domain : apiUrls.dev_domain;

// Initialize environment from storage if available
const initializeEnvironment = () => {
  try {
    const API_KEY = localStorage.getItem('API_KEY');
    if (API_KEY) {
      const savedEnvironment = localStorage.getItem('app_environment');
      if (savedEnvironment && (savedEnvironment === 'PRODUCTION' || savedEnvironment === 'SANDBOX')) {
        currentEnvironment = savedEnvironment;
        baseUrl = savedEnvironment === 'PRODUCTION' ? apiUrls.prod_domain : apiUrls.dev_domain;
      }
    } else {
      currentEnvironment = 'PRODUCTION';
      baseUrl = apiUrls.prod_domain;
    }
  } catch (e) { }
};

// Call initialization
initializeEnvironment();

// Function to switch environment
const switchEnvironment = (environment, shouldReload = true) => {
  if (environment === 'PRODUCTION') {
    currentEnvironment = 'PRODUCTION';
    baseUrl = apiUrls.prod_domain;
  } else {
    currentEnvironment = 'SANDBOX';
    baseUrl = apiUrls.dev_domain;
  }
  updateUrls();
  try {
    localStorage.setItem('app_environment', currentEnvironment);
  } catch (e) { }
  if (shouldReload) {
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }
  return currentEnvironment;
};

// Function to get current environment
const getCurrentEnvironment = () => {
  return currentEnvironment;
};

// Function to update URLs when baseUrl changes
const updateUrls = () => {
  urls.login = baseUrl + version + "login";
  urls.logout = baseUrl + version + "logout";
  urls.register = baseUrl + version + "register";
  urls.forgot_password = baseUrl + version + "forgot-password";
  urls.change_password = baseUrl + version + "change-password";
  urls.users = baseUrl + version + "users";
  urls.blockchain = baseUrl + version + "blockchain";
  urls.misc = baseUrl + version + "misc";
  urls.analytics = baseUrl + version + "analytics";
  urls.payments = baseUrl + version + "payments";
};
const urls = {
  login: baseUrl + version + "login",
  logout: baseUrl + version + "logout",
  register: baseUrl + version + "register",
  forgot_password: baseUrl + version + "forgot-password",
  change_password: baseUrl + version + "change-password",
  users: baseUrl + version + "users",
  blockchain: baseUrl + version + "blockchain",
  misc: baseUrl + version + "misc",
  media: apiUrls.upload_server + "media",
  analytics: baseUrl + version + "analytics",
  payments: baseUrl + version + "payments",

  documentation: apiUrls.documentation,
  openstreetmap: apiUrls.openstreetmap,
};

// Months and weeks arrays
const monthsArray = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
  .map((k) => i18n.t(k));
const weeksArray = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  .map((k) => i18n.t(k));

// Feature flags
const featureFlags = {
  mocks: (process.env.REACT_APP_FEATURE_FLAG_MOCKS || 'false').toLowerCase() === 'true',
};

// Analytics configuration
const analytics = {
  enabled: (process.env.REACT_APP_ENABLE_ANALYTICS || 'true').toLowerCase() === 'true',
  writeKey: process.env.REACT_APP_ANALYTICS_WRITE_KEY || '',
};

// Store data
const storeData = async (key, value) => {
  try {
    const jsonValue = JSON.stringify(value);
    await AsyncStorage.setItem(key, jsonValue);
  } catch (e) { }
};

// Get data
const getData = async (key) => {
  try {
    const jsonValue = await AsyncStorage.getItem(key);
    return jsonValue != null ? JSON.parse(jsonValue) : null;
  } catch (e) { }
};

// Clear data
const clearData = async () => {
  try {
    await localStorage.clear();
    await AsyncStorage.clear();
  } catch (e) { }
};

// Show toast 
const showToast = async (status, title, message) => {
  const raw = Array.isArray(message) ? message.join('\n') : (message || title || status);
  const content = (<div style={{ whiteSpace: 'pre-line' }}> {i18n.t(raw)} </div>);
  toast(content);
};

// Convert base64 to blob
const base64ToBlob = (base64, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

// Copy to clipboard with fallback
const copyToClipboard = async (value) => {
  try {
    await navigator.clipboard.writeText(value);
    showToast('success', 'COPIED', 'Copied to clipboard!');
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showToast('success', 'COPIED', 'Copied to clipboard!');
    } catch { showToast('error', 'FAILED', 'Oops! Failed to copy'); }
  }
};

// Get current position
const getCurrentPosition = () => {
  return new Promise((resolve) => {
    try {
      if (!navigator?.geolocation) {
        showToast('error', 'FAILED', 'Whoopty Doo! Geolocation is not supported by your browser');
        return resolve(null);
      }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const coords = pos.coords || {};
            const captured = {
              latitude: coords.latitude,
              longitude: coords.longitude,
              accuracy: coords.accuracy,
              altitude: coords.altitude,
              altitude_accuracy: coords.altitudeAccuracy,
              heading: coords.heading,
              speed: coords.speed,
              timestamp: pos.timestamp || Date.now(),
              address: "", address_2: "", city: "", state: "",
              postal_code: "", country: "", country_shortcode: "", formatted: "",
            };
            const geo = await getReverseGeocode(captured.latitude, captured.longitude);
            const merged = geo ? { ...captured, ...geo } : captured;
            resolve(merged);
          } catch (e) { resolve(null); }
        },
        (err) => {
          showToast('error', 'FAILED', err?.message || 'Whoopty Doo! Unable to capture location');
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 20000 }
      );
    } catch {
      showToast('error', 'FAILED', 'Whoopty Doo! Unable to capture location');
      resolve(null);
    }
  });
}

// Get reverse geocode
const getReverseGeocode = async (lat, lon) => {
  if (!lat || !lon) return null;
  try {
    const url = `${urls.openstreetmap}/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&zoom=18&addressdetails=1`;
    const resp = await fetch(url, { headers: { 'Accept': 'application/json', 'Accept-Language': i18n?.language || 'en' } });
    if (!resp.ok) return null;
    const data = await resp.json();
    const addr = data?.address || {};
    const formatted = data?.display_name || '';
    const streetCandidate = addr.road || addr.pedestrian || addr.residential || addr.path || addr.footway || addr.cycleway || '';
    const addressLine = (addr.house_number ? `${addr.house_number} ` : '') + (streetCandidate || '');
    const address2 = addr.neighbourhood || addr.suburb || addr.quarter || addr.district || '';
    const city = addr.city || addr.town || addr.village || addr.municipality || addr.hamlet || addr.locality || addr.county || '';
    const state = addr.state || addr.state_district || addr.region || addr.province || '';
    const postal = addr.postcode || '';
    const country = addr.country || '';
    const country_code = addr.country_code ? addr.country_code.toUpperCase() : '';
    return { address: addressLine || formatted, address_2: address2, city, state, postal_code: postal, country, country_code, formatted };
  } catch (e) { return null; }
}

// Format date
const formatDate = (ts) => {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(i18n.language || undefined);
  } catch { return ''; }
}

var constants = {
  urls, appVersion, baseUrl, version, acceptVersion, storeData, clearData, getData,
  showToast, base64ToBlob, monthsArray, weeksArray, switchEnvironment, getCurrentEnvironment,
  initializeEnvironment, copyToClipboard, getReverseGeocode, getCurrentPosition, formatDate,
  apiUrls, featureFlags, analytics, environment: { mode: envName, current: currentEnvironment, server: serverEnvironment }
};

export default constants